import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, Zap, Battery, Plug, Clock, DollarSign, Shield } from 'lucide-react';

const EVCharging = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              EV Charger Installation Services
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional electric vehicle charging station installation for homes and businesses in London, Kingston, and Ontario. Licensed electricians providing safe, code-compliant EV charger installations.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Get Free EV Charger Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Install EV Charger */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional EV Charger Installation
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                As electric vehicles become more popular in Ontario, having a reliable home or business charging station is essential. Studerus Electric Inc. specializes in professional EV charger installation, ensuring your charging station is safely installed and meets all electrical codes.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We install all major brands of Level 2 EV chargers, assess your electrical capacity, and handle all necessary permits and inspections.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Fast Charging at Home</h3>
                    <p className="text-gray-600">Level 2 chargers provide up to 40km of range per hour</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Licensed Installation</h3>
                    <p className="text-gray-600">ECRA/ESA certified electricians ensure safe, compliant installation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Increased Home Value</h3>
                    <p className="text-gray-600">EV charging adds value and appeal to your property</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Rebates Available</h3>
                    <p className="text-gray-600">Provincial and federal incentives can offset installation costs</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/yaoa5njb_20251003_140105.jpg"
                alt="EV charger installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Benefits of EV Charger Installation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card>
              <CardHeader>
                <Clock className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Convenience</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Charge your EV overnight at home - wake up to a fully charged vehicle every morning.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <DollarSign className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Cost Savings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Home charging is significantly cheaper than public charging stations or gasoline.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-yellow-500 mb-4" />
                <CardTitle>Fast Charging</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Level 2 chargers are 5-10x faster than standard 120V outlets, fully charging overnight.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-900 mb-4" />
                <CardTitle>Safety & Reliability</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Professional installation ensures safe operation and protects your home's electrical system.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our EV Charger Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Our EV Charger Installation Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Plug className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Residential EV Chargers</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Level 2 home charger installation</li>
                  <li>• Indoor & outdoor installations</li>
                  <li>• Wall-mounted or pedestal options</li>
                  <li>• WiFi-enabled smart chargers</li>
                  <li>• Panel capacity assessment</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Battery className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Commercial EV Charging</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Multi-unit charging stations</li>
                  <li>• Workplace charging solutions</li>
                  <li>• Retail & hospitality installations</li>
                  <li>• Fleet charging systems</li>
                  <li>• Load management systems</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-purple-600 mb-4" />
                <CardTitle>Electrical Upgrades</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Panel capacity upgrades</li>
                  <li>• Dedicated 40A or 50A circuits</li>
                  <li>• Service upgrade (100A to 200A)</li>
                  <li>• Conduit and wiring installation</li>
                  <li>• Breaker and disconnect installation</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-orange-600 mb-4" />
                <CardTitle>Permits & Inspections</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• ESA permit applications</li>
                  <li>• Code-compliant installations</li>
                  <li>• Inspection coordination</li>
                  <li>• Documentation & certificates</li>
                  <li>• Warranty support</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <DollarSign className="h-12 w-12 text-teal-600 mb-4" />
                <CardTitle>Rebate Assistance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Provincial rebate guidance</li>
                  <li>• Federal incentive programs</li>
                  <li>• Application support</li>
                  <li>• Documentation assistance</li>
                  <li>• Cost optimization advice</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Clock className="h-12 w-12 text-red-600 mb-4" />
                <CardTitle>Maintenance & Support</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Charger troubleshooting</li>
                  <li>• Repair services</li>
                  <li>• Software updates</li>
                  <li>• Annual inspections</li>
                  <li>• 24/7 emergency support</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Compatible EV Brands */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-6">
            Compatible with All EV Brands
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            We install Level 2 charging stations compatible with all electric vehicles sold in North America
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Tesla</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Chevrolet</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Ford</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">BMW</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Nissan</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Volkswagen</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Hyundai</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Kia</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Audi</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Mercedes</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Rivian</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="font-semibold text-gray-900">Polestar</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            EV Charger Installation Process
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Professional installation from consultation to activation
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-lg mb-2">Site Assessment</h3>
              <p className="text-gray-600 text-sm">Evaluate electrical capacity and optimal charger location</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-lg mb-2">Quote & Planning</h3>
              <p className="text-gray-600 text-sm">Detailed proposal with equipment and installation costs</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-lg mb-2">Permits</h3>
              <p className="text-gray-600 text-sm">Obtain necessary ESA permits and approvals</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-lg mb-2">Installation</h3>
              <p className="text-gray-600 text-sm">Professional charger and circuit installation</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                5
              </div>
              <h3 className="font-semibold text-lg mb-2">Testing & Training</h3>
              <p className="text-gray-600 text-sm">ESA inspection, testing, and charger operation training</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            EV Charger FAQs
          </h2>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>How long does EV charger installation take?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Most residential installations take 3-6 hours. If panel upgrades are needed, installation may take 1-2 days. We'll provide an accurate timeline during your free assessment.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What's the cost of EV charger installation?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Installation costs typically range from $500-$2,000 depending on distance from panel, electrical upgrades needed, and charger location. We provide detailed quotes including all equipment and labour.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Do I need to upgrade my electrical panel?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Not always. We assess your current electrical capacity during the site visit. Many homes with 100A or 200A service can accommodate a Level 2 charger. We'll recommend upgrades only if necessary.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Are there rebates available in Ontario?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes! Various federal and provincial programs offer rebates for EV charger installation. We help you navigate available incentives to maximize your savings.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Install Your EV Charger?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact Studerus Electric Inc. today for a free EV charger installation consultation and quote
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <Link to="/contact">Get Free Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EVCharging;
